import { useRef } from "react";
import { faTriangleExclamation } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Button } from "../../webapp/ui";
import { ContactUs } from "../../webapp/infoSection/ContactUs";
import { CloseIcon } from "../../webapp/icons/CloseIcon";
import { AUTH0_CLIENT_ID, AUTH0_DOMAIN } from "../../globals";
import { AUTH_CALLBACK_FAILED, AUTH_ERROR_KEY, FORCE_LOGIN_PARAM } from "./constants";

const buildAuth0LogoutUrl = (returnTo: string) => {
  const domain = AUTH0_DOMAIN.replace(/^https?:\/\//, "");
  const url = new URL(`https://${domain}/v2/logout`);
  url.searchParams.set("client_id", AUTH0_CLIENT_ID);
  url.searchParams.set("returnTo", returnTo);
  return url.toString();
};

/**
 * `reason` is a fallback for callers that render this screen in the same commit
 * that decides the failure, before the reason has been written to sessionStorage.
 * Stored state still wins, so a latched error survives a reload intact.
 */
export function BlockedUserMessage({ reason }: { reason?: string }) {
  const dialogRef = useRef<HTMLDialogElement>(null);

  const errorDetails = (() => {
    try {
      const stored = sessionStorage.getItem(AUTH_ERROR_KEY);
      if (stored) return JSON.parse(stored) as { errorParam?: string; errorDescription?: string };
    } catch {
      /* ignore */
    }
    return null;
  })();

  const errorParam = errorDetails?.errorParam ?? reason;
  const isLoginLoop = errorParam === "login_loop";
  const isCallbackFailed = errorParam === AUTH_CALLBACK_FAILED;
  const isRecoverableSignInFailure = isLoginLoop || isCallbackFailed;

  const handleReturnToLogin = () => {
    // Clearing local storage alone is not enough: Auth0's SSO cookie still
    // auto-completes login. End the Auth0 session, then force the login form.
    sessionStorage.clear();
    localStorage.clear();
    const returnTo = new URL(window.location.origin);
    returnTo.searchParams.set(FORCE_LOGIN_PARAM, "1");
    window.location.href = buildAuth0LogoutUrl(returnTo.toString());
  };

  return (
    <div
      data-testid="auth-error-screen"
      data-error-param={errorParam ?? "account_blocked"}
      className="flex flex-col items-center justify-center min-h-screen bg-[var(--color-background)]"
    >
      <div className="flex flex-col items-center max-w-[351px] p-6">
        <div className="mb-4">
          <FontAwesomeIcon
            icon={faTriangleExclamation}
            style={{
              width: 35,
              height: 32.5,
              color: isRecoverableSignInFailure ? "var(--semantic-warning)" : "var(--semantic-danger)",
            }}
            aria-hidden="true"
            focusable="false"
          />
        </div>
        <div className="text-[var(--color-foreground)] text-[length:var(--font-size-lg,16px)] font-semibold mb-2">
          {isCallbackFailed ? "Sign-in failed" : isLoginLoop ? "Something went wrong" : "Account access denied"}
        </div>
        <div className="text-[var(--color-foreground-muted)] text-[length:var(--font-size-base,13px)] font-normal text-center mb-6">
          {isCallbackFailed
            ? "We couldn't complete sign-in. Please try again, or contact Support if this keeps happening."
            : isLoginLoop
              ? "We were unable to log you in after multiple attempts. Please try again later or contact Support."
              : "It looks like this account has been blocked. If you think this is a mistake, please contact Support."}
        </div>
        <div className="flex gap-3 justify-center w-full">
          <Button variant="outline" color="neutral" onClick={handleReturnToLogin}>
            Return to Log In Page
          </Button>
          <Button
            variant="outline"
            color="primary"
            onClick={(e) => {
              (e.target as HTMLButtonElement).blur();
              dialogRef.current?.showModal();
            }}
          >
            Contact Support
          </Button>
        </div>
      </div>

      <dialog
        ref={dialogRef}
        className="rounded-md shadow-xl border border-[var(--outline-default)] p-0 backdrop:bg-black/50 fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 m-0"
        style={{ backgroundColor: "var(--toast-default)", color: "var(--toast-foreground)" }}
        onClick={(e) => {
          if (e.target === dialogRef.current) dialogRef.current?.close();
        }}
      >
        <div className="p-[var(--spacing-scale-medium-500,15px)] min-w-[350px]">
          <div className="flex justify-between items-center mb-2">
            <h4
              style={{
                color: "var(--toast-foreground)",
                fontSize: "var(--font-size-lg, 13.5px)",
                fontWeight: "var(--font-weights-semibold, 600)",
                lineHeight: "var(--line-height-text-lg, 21px)",
              }}
            >
              Contact Us
            </h4>
            <CloseIcon onClick={() => dialogRef.current?.close()} />
          </div>
          <ContactUs />
          <div className="flex justify-end mt-4">
            <Button variant="outline" color="neutral" onClick={() => dialogRef.current?.close()}>
              Close
            </Button>
          </div>
        </div>
      </dialog>
    </div>
  );
}
